// Organisation-wide settings: the organisation itself, its facilities and branches, and
// the registration settings with the consent wording (Settings > Organisation and
// Settings > Registration). The API checks every permission; the screens only hide what
// the person can't use.
import {
  consentTextSchema,
  facilitySchema,
  organisationSchema,
  registrationSettingsSchema,
  type consentTextInputSchema,
  type createFacilityBodySchema,
  type updateFacilityBodySchema,
  type updateOrganisationBodySchema,
  type updateRegistrationSettingsRequestSchema,
} from '@wodi/shared';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { api } from './client.ts';

export const organisationKeys = {
  organisation: ['admin', 'organisation'] as const,
  facilities: ['admin', 'facilities'] as const,
  consentTexts: ['admin', 'consent-texts'] as const,
  registrationSettings: ['admin', 'registration-settings'] as const,
};

export const organisationQuery = queryOptions({
  queryKey: organisationKeys.organisation,
  queryFn: () => api('GET', '/organisation', { schema: organisationSchema }),
});

export function updateOrganisation(body: z.input<typeof updateOrganisationBodySchema>) {
  return api('PATCH', '/organisation', { body, schema: organisationSchema });
}

export const facilitiesQuery = queryOptions({
  queryKey: organisationKeys.facilities,
  queryFn: () => api('GET', '/facilities', { schema: z.array(facilitySchema) }),
});

export function createFacility(body: z.input<typeof createFacilityBodySchema>) {
  return api('POST', '/facilities', { body, schema: facilitySchema });
}

export function updateFacility(id: string, body: z.input<typeof updateFacilityBodySchema>) {
  return api('PATCH', `/facilities/${id}`, { body, schema: facilitySchema });
}

export const adminConsentTextsQuery = queryOptions({
  queryKey: organisationKeys.consentTexts,
  queryFn: () => api('GET', '/admin/consent-texts', { schema: z.array(consentTextSchema) }),
});

export function createConsentText(body: z.input<typeof consentTextInputSchema>) {
  return api('POST', '/admin/consent-texts', { body, schema: consentTextSchema });
}

export function retireConsentText(id: string) {
  return api('POST', `/admin/consent-texts/${id}/retire`, { schema: consentTextSchema });
}

export const adminRegistrationSettingsQuery = queryOptions({
  queryKey: organisationKeys.registrationSettings,
  queryFn: () => api('GET', '/admin/registration-settings', { schema: registrationSettingsSchema }),
});

export function saveRegistrationSettings(
  body: z.input<typeof updateRegistrationSettingsRequestSchema>,
) {
  return api('PUT', '/admin/registration-settings', { body, schema: registrationSettingsSchema });
}
